import ENV from 'env.json';
import { PopUp } from 'interfaces';

const STORAGE_KEY = '30PAY__POPUP_DATA';

type PopupViewLog = Record<number, { viewedCount: number; lastViewed: string }>;

/** Map admin urlPage values (xpay-style) onto 30pay user routes. */
const PATH_ALIASES: Record<string, string[]> = {
    '/dashboard': ['/'],
    '/orders/create-order': ['/trade'],
    '/orders': ['/reports'],
    '/wallet/deposit/IRT': ['/wallet/deposit'],
    '/wallet/withdrawal/IRT': ['/wallet/withdrawal'],
    '/wallet/deposit/coin': ['/wallet/deposit'],
    '/wallet/withdrawal/coin': ['/wallet/withdrawal'],
    '/authentication': ['/profile'],
    '/bank-accounts': ['/cards'],
    '/bank-accounts/add-card': ['/cards'],
    '/bank-accounts/add-iban': ['/cards'],
    '/notifications': ['/profile'],
};

export const getPopupViewLog = (): PopupViewLog => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
        return JSON.parse(raw);
    } catch {
        return {};
    }
};

export const logPopupView = (popupId: number) => {
    const log = getPopupViewLog();
    const current = log[popupId];
    log[popupId] = {
        viewedCount: (current?.viewedCount ?? 0) + 1,
        lastViewed: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
};

export const resolvePopUpImageUrl = (imageUrl?: string | null): string | undefined => {
    if (!imageUrl) return undefined;
    if (/^(https?:|blob:|data:)/i.test(imageUrl)) return imageUrl;
    const origin = ENV.api.replace(/\/v1\/?$/, '');
    return `${origin}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
};

const normalizePath = (p: string): string => {
    if (!p) return '/';
    let s = p.trim();
    try {
        s = new URL(s, window.location.origin).pathname;
    } catch {
        s = s.split('?')[0].split('#')[0];
    }
    if (!s.startsWith('/')) s = `/${s}`;
    s = s.replace(/\/{2,}/g, '/');
    if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
    return s;
};

const matchesTarget = (rawUrlPage: string, currentPath: string): boolean => {
    const isStarWildcard = /\/\*$/.test(rawUrlPage);
    const hasTrailingSlashOnly = /\/$/.test(rawUrlPage) && !isStarWildcard && rawUrlPage !== '/';
    const cleaned = isStarWildcard
        ? rawUrlPage.replace(/\/\*$/, '')
        : hasTrailingSlashOnly
          ? rawUrlPage.replace(/\/$/, '')
          : rawUrlPage;
    const target = normalizePath(cleaned);
    const current = normalizePath(currentPath);

    if (isStarWildcard) return current === target || current.startsWith(`${target}/`);
    if (hasTrailingSlashOnly) return current.startsWith(`${target}/`);
    return current === target;
};

const expandUrlPage = (urlPage: string): string[] => {
    const trimmed = urlPage.trim();
    const variants = [trimmed];
    if (trimmed.startsWith('/dashboard')) {
        const stripped = trimmed.replace(/^\/dashboard/, '') || '/';
        variants.push(stripped);
    }
    const withoutStar = trimmed.replace(/\/\*$/, '');
    (PATH_ALIASES[withoutStar] || []).forEach((alias) => variants.push(alias));
    return variants;
};

export const canShowPopup = (popup: PopUp, currentPath: string): boolean => {
    if (popup.urlPage && popup.urlPage !== '/') {
        const matches = expandUrlPage(popup.urlPage).some((candidate) => matchesTarget(candidate, currentPath));
        if (!matches) return false;
    }

    const log = getPopupViewLog();
    const lastViewed = log[popup.id]?.lastViewed ? new Date(log[popup.id].lastViewed) : null;

    if (!lastViewed) return popup.viewCount > 0;

    const hoursSinceLastView = (Date.now() - lastViewed.getTime()) / (1000 * 60 * 60);
    if (hoursSinceLastView < popup.intervalView * 24) return false;
    if (log[popup.id].viewedCount >= popup.viewCount) return false;

    return true;
};

export const pickEligiblePopups = (popups: PopUp[], currentPath: string, excludeIds: number[] = []): PopUp[] =>
    popups
        .filter((popup) => !excludeIds.includes(popup.id) && canShowPopup(popup, currentPath))
        .sort((a, b) => a.viewAfterSecond - b.viewAfterSecond);
